import { cn } from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

export type ScoreBadgeTone =
  "success" | "success-alt" | "warning" | "destructive" | "muted";

export interface ScoreBadgeProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Semantic color of the pill. Drives the bg / border-color / text triplet.
   * The consumer maps its own domain rating onto one of these tones.
   */
  tone: ScoreBadgeTone;
  /**
   * When `true`, the `border border-solid` width+style is dropped (the
   * tone's `border-<color>` class remains but paints nothing without a
   * width). Defaults to `false` => bordered. Mirrors `PercentBadge`'s
   * `borderless` prop; `CsatScore` always renders bordered (omits it).
   */
  borderless?: boolean;
}

const toneClassName: Record<ScoreBadgeTone, string> = {
  success: "bg-success-background border-success text-success",
  "success-alt": "bg-success-background border-primary text-success",
  warning: "bg-warning-background border-warning text-warning",
  destructive: "bg-destructive-background border-destructive text-destructive",
  muted: "bg-muted border-muted text-muted-foreground",
};

/**
 * Presentational leaf: a small bordered pill rendering a tone-driven chip.
 * Mirrors the sibling `Duration` block — a single `forwardRef` div with a
 * `data-slot`, `cn`-merged className, and full rest-prop spread.
 *
 * It carries NO domain knowledge: no range helpers, no CSAT / percentage
 * notion, no i18n. The consumer maps its own rating onto a `tone` and feeds
 * the already-formatted text via `children`.
 *
 * The five tone triplets are kept distinct on purpose (note `success-alt`
 * uses `border-primary`, not `border-success`). When `borderless` is true the
 * `border border-solid` width+style is dropped; the tone's `border-<color>`
 * stays but paints nothing without a width, exactly how `PercentBadge` behaves.
 *
 * ```tsx
 * <ScoreBadge tone="success" data-test="csat-score-95">95</ScoreBadge>
 * <ScoreBadge tone="success-alt" borderless data-test="percent-badge-78">78 %</ScoreBadge>
 * ```
 */
export const ScoreBadge = forwardRef<HTMLDivElement, ScoreBadge.Props>(
  (
    componentProps: ScoreBadge.Props,
    forwardRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      tone,
      borderless = false,
      className,
      children,
      ...props
    } = componentProps;

    return (
      <div
        ref={forwardRef}
        data-slot="score-badge"
        className={cn(
          "flex w-fit items-center justify-center rounded-lg px-1 py-0.5 text-center text-base font-semibold cursor-default",
          !borderless && "border border-solid",
          toneClassName[tone],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
ScoreBadge.displayName = "ScoreBadge";

export namespace ScoreBadge {
  export type Props = ScoreBadgeProps;
}
